import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function dotKey(row, col) {
  return `${row},${col}`;
}

function parseDotKey(key) {
  const [row, col] = String(key).split(',').map((value) => Number(value));
  return { row, col };
}

function sortDotKeys(a, b) {
  const aPoint = parseDotKey(a);
  const bPoint = parseDotKey(b);
  if (aPoint.row !== bPoint.row) return aPoint.row - bPoint.row;
  return aPoint.col - bPoint.col;
}

function edgeKey(a, b) {
  const points = [a, b].sort(sortDotKeys);
  return `${points[0]}|${points[1]}`;
}

function twiceTriangleArea(a, b, c) {
  return Math.abs(
    (a.x * (b.y - c.y))
    + (b.x * (c.y - a.y))
    + (c.x * (a.y - b.y))
  );
}

function pointInsideOrOnTriangle(point, a, b, c) {
  const totalArea = twiceTriangleArea(a, b, c);
  if (totalArea === 0) return false;
  const area1 = twiceTriangleArea(point, b, c);
  const area2 = twiceTriangleArea(a, point, c);
  const area3 = twiceTriangleArea(a, b, point);
  return area1 + area2 + area3 === totalArea;
}

function orientation(p, q, r) {
  const value = ((q.x - p.x) * (r.y - p.y)) - ((q.y - p.y) * (r.x - p.x));
  if (value === 0) return 0;
  return value > 0 ? 1 : -1;
}

function pointOnSegment(point, a, b) {
  if (orientation(a, b, point) !== 0) return false;
  const withinY = point.y >= Math.min(a.y, b.y) && point.y <= Math.max(a.y, b.y);
  const withinX = point.x >= Math.min(a.x, b.x) && point.x <= Math.max(a.x, b.x);
  return withinY && withinX;
}

function getPlayerIdentity(player = {}) {
  return player.socketId || player.userId || player.id || player.username;
}

function TrianglesGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const svgRef = useRef(null);
  const [dragState, setDragState] = useState(null);

  const board = gameState?.board || { size: 5, rows: 5, cols: 5 };
  const size = Number(board.size || board.rows || board.cols) || 5;
  const lines = useMemo(() => gameState?.lines || {}, [gameState?.lines]);
  const claimedTriangles = useMemo(() => gameState?.claimedTriangles || {}, [gameState?.claimedTriangles]);
  const claimedTriangleColors = useMemo(() => gameState?.claimedTriangleColors || {}, [gameState?.claimedTriangleColors]);
  const boardDots = useMemo(() => Array.isArray(board?.dots) ? board.dots : [], [board?.dots]);
  const players = useMemo(() => {
    const merged = new Map();
    const livePlayers = Array.isArray(gamePlayers) ? gamePlayers : [];
    const statePlayers = Array.isArray(gameState?.players) ? gameState.players : [];

    livePlayers.forEach((player) => {
      const id = getPlayerIdentity(player);
      if (!id) return;
      merged.set(id, { ...player, id });
    });

    statePlayers.forEach((player) => {
      const id = getPlayerIdentity(player);
      if (!id) return;
      const livePlayer = merged.get(id) || {};
      merged.set(id, {
        ...player,
        ...livePlayer,
        id,
        score: typeof player?.score !== 'undefined' ? player.score : livePlayer.score,
      });
    });

    return Array.from(merged.values());
  }, [gamePlayers, gameState?.players]);

  const playerMap = useMemo(() => Object.fromEntries(players.map((player) => [player.id, player])), [players]);
  const currentPlayer = playerMap[gameState?.currentPlayer] || null;
  const canInteract = !gameState?.finished && gameState?.currentPlayer === user?.socketId;

  const padding = 48;
  const dotRadius = 6;
  const hitRadius = 18;
  const nearestThreshold = 28;

  const dots = useMemo(() => {
    if (boardDots.length > 0) {
      return boardDots.map((dot) => ({
        key: dot.id,
        row: Number(dot.id?.split(',')[0]),
        col: Number(dot.id?.split(',')[1]),
        x: Number(dot.x) + padding,
        y: Number(dot.y) + padding,
      }));
    }

    const entries = [];
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        entries.push({
          key: dotKey(row, col),
          row,
          col,
          x: padding + col * 100,
          y: padding + row * 100,
        });
      }
    }
    return entries;
  }, [boardDots, padding, size]);

  const bounds = useMemo(() => {
    if (dots.length === 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
    return dots.reduce((acc, dot) => ({
      minX: Math.min(acc.minX, dot.x),
      maxX: Math.max(acc.maxX, dot.x),
      minY: Math.min(acc.minY, dot.y),
      maxY: Math.max(acc.maxY, dot.y),
    }), { minX: Number.POSITIVE_INFINITY, maxX: Number.NEGATIVE_INFINITY, minY: Number.POSITIVE_INFINITY, maxY: Number.NEGATIVE_INFINITY });
  }, [dots]);
  const viewWidth = Math.max(160, bounds.maxX - bounds.minX + padding * 2);
  const viewHeight = Math.max(160, bounds.maxY - bounds.minY + padding * 2);

  const dotMap = useMemo(() => Object.fromEntries(dots.map((dot) => [dot.key, dot])), [dots]);
  const triangleDefinitions = useMemo(() => {
    const legalTriangles = gameState?.legalTriangles || {};
    return Object.entries(legalTriangles).map(([id, triangle]) => ({
      id,
      vertices: Array.isArray(triangle?.vertices) ? triangle.vertices : [],
    }));
  }, [gameState?.legalTriangles]);
  const claimedTriangleDefinitions = useMemo(() => triangleDefinitions
    .filter((triangle) => claimedTriangles[triangle.id])
    .map((triangle) => ({
      ...triangle,
      points: triangle.vertices.map((vertexKey) => dotMap[vertexKey]).filter(Boolean),
    }))
    .filter((triangle) => triangle.points.length === 3), [claimedTriangles, dotMap, triangleDefinitions]);

  const renderedTriangles = useMemo(() => triangleDefinitions
    .filter((triangle) => claimedTriangles[triangle.id])
    .map((triangle) => {
      const ownerId = claimedTriangles[triangle.id];
      const owner = playerMap[ownerId] || {};
      const color = owner.color || claimedTriangleColors[triangle.id] || '#38bdf8';
      return {
        ...triangle,
        ownerId,
        owner,
        color,
        points: triangle.vertices.map((vertexKey) => {
          const point = dotMap[vertexKey];
          return `${point?.x || 0},${point?.y || 0}`;
        }).join(' '),
      };
    }), [claimedTriangleColors, claimedTriangles, dotMap, playerMap, triangleDefinitions]);

  const renderedLines = useMemo(() => Object.entries(lines)
    .map(([lineId, ownerId]) => {
      const [startKey, endKey] = lineId.split('|');
      const start = dotMap[startKey];
      const end = dotMap[endKey];
      if (!start || !end) return null;
      const owner = playerMap[ownerId] || {};
      return {
        lineId,
        ownerId,
        owner,
        x1: start.x,
        y1: start.y,
        x2: end.x,
        y2: end.y,
      };
    })
    .filter(Boolean), [dotMap, lines, playerMap]);

  const playerRows = useMemo(() => players.map((player) => ({
    id: player.id,
    username: player.username || 'Player',
    color: player.color || '#38bdf8',
    icon: player.icon || null,
    score: Number(gameState?.scores?.[player.id]) || 0,
    isCurrent: player.id === gameState?.currentPlayer,
    isWinner: Array.isArray(gameState?.winners) && gameState.winners.includes(player.id),
  })), [gameState?.currentPlayer, gameState?.scores, gameState?.winners, players]);

  const getSvgPoint = (event) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const transformed = point.matrixTransform(ctm.inverse());
    return { x: transformed.x, y: transformed.y };
  };

  const getNearestDot = (point) => {
    let nearest = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const dot of dots) {
      const distance = Math.hypot(dot.x - point.x, dot.y - point.y);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = dot;
      }
    }
    return nearestDistance <= nearestThreshold ? nearest : null;
  };

  const analyzeConnection = (fromDot, toDot) => {
    if (!fromDot || !toDot) return { isValid: false, invalidDotKeys: [] };
    if (fromDot.key === toDot.key) {
      return { isValid: false, invalidDotKeys: [fromDot.key] };
    }

    const lineId = edgeKey(fromDot.key, toDot.key);
    if (lines[lineId]) {
      return { isValid: false, invalidDotKeys: [fromDot.key, toDot.key] };
    }

    const definition = gameState?.legalLines?.[lineId] || null;
    if (!definition) {
      const blockingDots = dots
        .filter((dot) => dot.key !== fromDot.key && dot.key !== toDot.key)
        .filter((dot) => pointOnSegment(dot, fromDot, toDot))
        .map((dot) => dot.key);
      return { isValid: false, invalidDotKeys: blockingDots };
    }
    if (Array.isArray(definition.crosses)) {
      const crossingLineId = definition.crosses.find((candidateLineId) => !!lines[candidateLineId]);
      if (crossingLineId) {
        return {
          isValid: false,
          invalidDotKeys: crossingLineId.split('|'),
        };
      }
    }

    const containingTriangle = claimedTriangleDefinitions.find((triangle) => pointInsideOrOnTriangle(
      fromDot,
      triangle.points[0],
      triangle.points[1],
      triangle.points[2],
    ) && pointInsideOrOnTriangle(
      toDot,
      triangle.points[0],
      triangle.points[1],
      triangle.points[2],
    ));
    if (containingTriangle) {
      return { isValid: false, invalidDotKeys: containingTriangle.vertices.slice() };
    }

    return { isValid: true, invalidDotKeys: [] };
  };

  const startDrag = (event, dot) => {
    if (!canInteract) return;
    event.preventDefault();
    svgRef.current?.setPointerCapture?.(event.pointerId);
    const point = getSvgPoint(event);
    setDragState({
      pointerId: event.pointerId,
      fromKey: dot.key,
      point,
      targetKey: null,
      isValid: false,
      invalidDotKeys: [],
    });
  };

  const updateDrag = (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    const point = getSvgPoint(event);
    const fromDot = dotMap[dragState.fromKey];
    const nearestDot = getNearestDot(point);
    const targetKey = nearestDot?.key || null;
    const connection = fromDot && nearestDot
      ? analyzeConnection(fromDot, nearestDot)
      : { isValid: false, invalidDotKeys: [] };
    setDragState((current) => current ? {
      ...current,
      point,
      targetKey,
      isValid: connection.isValid,
      invalidDotKeys: targetKey ? connection.invalidDotKeys : [],
    } : current);
  };

  const endDrag = (event) => {
    if (!dragState || event.pointerId !== dragState.pointerId) return;
    svgRef.current?.releasePointerCapture?.(event.pointerId);

    const fromDot = dotMap[dragState.fromKey];
    const toDot = dragState.targetKey ? dotMap[dragState.targetKey] : null;
    const shouldSubmit = !!fromDot && !!toDot && dragState.isValid;
    setDragState(null);

    if (shouldSubmit) {
      submitAction({
        type: 'draw-line',
        from: { row: fromDot.row, col: fromDot.col },
        to: { row: toDot.row, col: toDot.col },
      }).catch(() => {});
    }
  };

  useEffect(() => {
    if (!dragState) return undefined;
    const cancelDrag = () => setDragState(null);
    window.addEventListener('pointercancel', cancelDrag);
    return () => window.removeEventListener('pointercancel', cancelDrag);
  }, [dragState]);

  const previewLine = useMemo(() => {
    if (!dragState) return null;
    const fromDot = dotMap[dragState.fromKey];
    if (!fromDot) return null;
    const targetDot = dragState.targetKey ? dotMap[dragState.targetKey] : null;
    return {
      x1: fromDot.x,
      y1: fromDot.y,
      x2: targetDot?.x ?? dragState.point.x,
      y2: targetDot?.y ?? dragState.point.y,
      isValid: dragState.isValid,
    };
  }, [dotMap, dragState]);

  const pulsingDotKeys = useMemo(() => {
    if (!dragState || dragState.isValid) return new Set();
    return new Set(Array.isArray(dragState.invalidDotKeys) ? dragState.invalidDotKeys : []);
  }, [dragState]);

  const winnerNames = useMemo(() => {
    const winners = Array.isArray(gameState?.winners) ? gameState.winners : [];
    return winners.map((winnerId) => playerMap[winnerId]?.username || 'Player');
  }, [gameState?.winners, playerMap]);

  return (
    <div className="p-4 text-slate-900 dark:text-slate-100">
      <div className="flex flex-col gap-4">
        <div className="rounded-2xl border border-slate-300 bg-slate-100 p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            
            <div className="text-left text-sm">
              <div className="text-slate-600 dark:text-slate-300">
                {gameState?.finished
                  ? 'Round complete'
                  : currentPlayer
                    ? `${currentPlayer.username || 'Player'} to draw`
                    : 'Waiting for players'}
              </div>
              <div className={canInteract ? 'font-semibold text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'}>
                {canInteract ? 'Drag between any two unobstructed dots to place your line' : 'Watch the board or wait for your turn'}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="mx-auto w-full max-w-4xl rounded-3xl border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-white to-amber-50 p-3 shadow-[0_16px_48px_rgba(15,23,42,0.08)] dark:border-cyan-900/50 dark:from-slate-950 dark:via-slate-900 dark:to-cyan-950/60 sm:p-5">
              <svg
                ref={svgRef}
                viewBox={`0 0 ${viewWidth} ${viewHeight}`}
                className="w-full h-auto touch-none select-none"
                onPointerMove={updateDrag}
                onPointerUp={endDrag}
              >
                {renderedTriangles.map((triangle) => (
                  <g key={triangle.id}>
                    <polygon
                      points={triangle.points}
                      fill={triangle.color}
                      fillOpacity="0.72"
                      stroke={triangle.color}
                      strokeOpacity="0.55"
                      strokeWidth="2"
                    />
                  </g>
                ))}

                {renderedLines.map((line) => (
                  <line
                    key={line.lineId}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={line.owner?.color || '#0f172a'}
                    strokeWidth="8"
                    strokeLinecap="round"
                  />
                ))}

                {previewLine ? (
                  <line
                    x1={previewLine.x1}
                    y1={previewLine.y1}
                    x2={previewLine.x2}
                    y2={previewLine.y2}
                    stroke={previewLine.isValid ? '#22c55e' : '#ef4444'}
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray="10 7"
                    opacity="0.95"
                  />
                ) : null}

                {dots.map((dot) => (
                  <g key={dot.key}>
                    {pulsingDotKeys.has(dot.key) ? (
                      <circle cx={dot.x} cy={dot.y} r={dotRadius + 4} fill="#ef4444" opacity="0.3">
                        <animate attributeName="r" values={`${dotRadius + 3};${dotRadius + 11};${dotRadius + 3}`} dur="0.9s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.65;0.12;0.65" dur="0.9s" repeatCount="indefinite" />
                      </circle>
                    ) : null}
                    <circle cx={dot.x} cy={dot.y} r={dotRadius} className="fill-slate-900 dark:fill-slate-100" />
                    <circle
                      cx={dot.x}
                      cy={dot.y}
                      r={hitRadius}
                      fill="transparent"
                      onPointerDown={(event) => startDrag(event, dot)}
                    />
                  </g>
                ))}
              </svg>
            </div>
          </div>

          {gameState?.finished ? (
            <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-100">
              {winnerNames.length === 0
                ? 'Game over.'
                : winnerNames.length === 1
                  ? `${winnerNames[0]} wins.`
                  : `Draw between ${winnerNames.join(', ')}.`}
            </div>
          ) : null}
        </div>

        <div className="mx-auto w-full max-w-3xl rounded-2xl border border-slate-300 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
          <h4 className="mb-3 font-semibold">Players</h4>
          <div className="space-y-2">
            {playerRows.map((player) => (
              <div
                key={player.id}
                className={`rounded-lg border px-3 py-2 ${player.isCurrent ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950/30' : 'border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 font-medium">
                    <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: player.color }} />
                    <span>{player.username}</span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {player.isWinner ? 'Winner' : player.isCurrent ? 'Current turn' : 'Waiting'}
                  </div>
                </div>
                <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                  {player.score} triangle{player.score === 1 ? '' : 's'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

TrianglesGame.Guide = function TrianglesGuide() {
  return (
    <div>
      <h3 className="mb-2 font-bold">How to Play Triangles</h3>
      <p>Players take turns drawing one line between nearby dots.</p>
      <p>Players can connect any two dots as long as the segment does not pass through another dot and does not cross an existing line.</p>
      <p>If your new line closes one or more of the smallest empty triangles, you claim them immediately and score a point for each one.</p>
      <p>When no legal lines remain, the player with the most claimed triangles wins.</p>
    </div>
  );
};

export default TrianglesGame;